import React, { useState, useEffect, useRef } from "react";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { IconLoader2, IconCheck, IconArrowLeft, IconShieldLock } from "@tabler/icons-react";
import { cpAdminSendVerification, cpAdminVerifyCode } from "_graphql/mutations";
import { usePortalSettings } from "_contexts/usePortalSettings";
import "./AdminTwoFactor.scss";

// Background images
import FieldsImage from "_assets/images/customer-portal/fields.jpg";
import HillsImage from "_assets/images/customer-portal/hills.jpg";
import MountainsImage from "_assets/images/customer-portal/mountains.jpg";
import PoppiesImage from "_assets/images/customer-portal/poppies.jpg";
import SunsetImage from "_assets/images/customer-portal/sunset.jpg";

const backgroundImages = {
  fields: FieldsImage,
  hills: HillsImage,
  mountains: MountainsImage,
  poppies: PoppiesImage,
  sunset: SunsetImage,
};

const CODE_LENGTH = 6;
const RESEND_COUNTDOWN = 30;

function AdminTwoFactor({ loginData, onSuccess, onBack }) {
  const client = useApolloClient();
  const { settings } = usePortalSettings();
  const inputRefs = useRef([]);

  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [channel, setChannel] = useState("sms");
  const [status, setStatus] = useState("idle"); // 'idle', 'sending', 'sent', 'verifying', 'expired'
  const [seconds, setSeconds] = useState(RESEND_COUNTDOWN);
  const [intervalId, setIntervalId] = useState(null);
  const [maskedDestination, setMaskedDestination] = useState("");

  const { userId } = loginData;

  // Get company settings
  const logoUrl = settings?.logo_url;
  const companyName = settings?.company?.name || "Paypath";
  const backgroundImage = settings?.background_image || "poppies";
  const bgImageUrl = backgroundImages[backgroundImage] || backgroundImages.poppies;

  // Send verification on mount
  useEffect(() => {
    sendVerification(channel);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-verify when code is complete
  useEffect(() => {
    const fullCode = code.join("");
    if (fullCode.length === CODE_LENGTH && status === "idle") {
      verifyCode(fullCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const startCountdown = () => {
    if (intervalId) clearInterval(intervalId);
    setSeconds(RESEND_COUNTDOWN);

    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
  };

  const sendVerification = async (verifyChannel) => {
    setStatus("sending");

    try {
      const result = await client.mutate({
        mutation: cpAdminSendVerification,
        variables: {
          user_id: userId,
          channel: verifyChannel,
        },
      });

      const send = result?.data?.cpAdminSendVerification;

      if (send?.success) {
        setMaskedDestination(send.masked_destination);
        setStatus("sent");
        message.success(`Verification code sent to ${send.masked_destination}`);

        setTimeout(() => {
          setStatus("idle");
          startCountdown();
          inputRefs.current[0]?.focus();
        }, 1500);
      } else {
        message.error(send?.error || "Failed to send code. Please try again.");
        setStatus("idle");
      }
    } catch (err) {
      console.error("Send verification error:", err);
      message.error("Failed to send code. Please try again.");
      setStatus("idle");
    }
  };

  const handleInputChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-focus next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    const digits = pastedData.slice(0, CODE_LENGTH).split("");

    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < CODE_LENGTH) {
        newCode[i] = digit;
      }
    });
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((d) => !d);
    const focusIndex = nextEmptyIndex === -1 ? CODE_LENGTH - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const verifyCode = async (fullCode) => {
    setStatus("verifying");

    try {
      const result = await client.mutate({
        mutation: cpAdminVerifyCode,
        variables: {
          user_id: userId,
          code: fullCode,
          channel: channel,
        },
      });

      const verify = result?.data?.cpAdminVerifyCode;

      if (verify?.success && verify?.token) {
        message.success("Verification successful!");
        onSuccess(verify.token);
      } else {
        if (verify?.error?.toLowerCase().includes("expired")) {
          setStatus("expired");
          message.error("Verification code has expired. Please request a new code.");
        } else {
          message.error(verify?.error || "Invalid code. Please try again.");
          setStatus("idle");
          setCode(Array(CODE_LENGTH).fill(""));
          inputRefs.current[0]?.focus();
        }
      }
    } catch (err) {
      console.error("Verify error:", err);
      message.error("Failed to verify code. Please try again.");
      setStatus("idle");
      setCode(Array(CODE_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const resendCode = async () => {
    if (seconds > 0) return;
    await sendVerification(channel);
  };

  const switchChannel = async () => {
    const newChannel = channel === "sms" ? "email" : "sms";
    setChannel(newChannel);
    setCode(Array(CODE_LENGTH).fill(""));
    await sendVerification(newChannel);
  };

  const renderStatusIndicator = () => {
    if (status === "sending") {
      return (
        <div className="status-indicator sending">
          <IconLoader2 className="status-icon spinning" size={18} />
          <span>Sending code...</span>
        </div>
      );
    }

    if (status === "sent") {
      return (
        <div className="status-indicator sent">
          <IconCheck className="status-icon" size={16} />
          <span>Code sent!</span>
        </div>
      );
    }

    if (status === "verifying") {
      return (
        <div className="status-indicator verifying">
          <IconLoader2 className="status-icon spinning" size={18} />
          <span>Validating code...</span>
        </div>
      );
    }

    if (status === "expired") {
      return (
        <button className="request-new-code-button" onClick={resendCode}>
          Request a new code
        </button>
      );
    }

    // Default: show countdown or resend button
    if (seconds > 0) {
      return (
        <div className="status-indicator countdown">
          <IconLoader2 className="status-icon" size={18} />
          <span>Request new code in {seconds} seconds</span>
        </div>
      );
    }

    return (
      <div className="resend-actions">
        <button className="resend-link" onClick={resendCode}>
          Request a new verification code
        </button>
        <button className="switch-link" onClick={switchChannel}>
          Use {channel === "sms" ? "email" : "SMS"} instead
        </button>
      </div>
    );
  };

  return (
    <div className="admin-twofactor-container">
      <div
        className="auth-background"
        style={{ backgroundImage: `url(${bgImageUrl})` }}
      />

      <div className="auth-modal">
        <button className="back-button" onClick={onBack}>
          <IconArrowLeft size={20} />
        </button>

        <div className="auth-content">
          {logoUrl && (
            <div className="brand-icon">
              <img src={logoUrl} alt={companyName} />
            </div>
          )}

          {/* Admin Badge */}
          <div className="admin-badge">
            <IconShieldLock size={16} />
            <span>Admin Access</span>
          </div>

          <div className="verify-section">
            <h1 className="verify-title">Enter Verification Code</h1>
            <p className="verify-subtitle">
              {maskedDestination ? (
                <>A verification code was sent to <strong>{maskedDestination}</strong></>
              ) : (
                <>Sending verification code...</>
              )}
            </p>
          </div>

          {/* OTP Input Boxes */}
          <div className="otp-input-container">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`otp-input ${digit ? "filled" : ""}`}
                disabled={status === "verifying" || status === "sending"}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {renderStatusIndicator()}
        </div>
      </div>
    </div>
  );
}

export default AdminTwoFactor;

